import { useState, useMemo, useRef } from 'react';
import {
  Box, Button, Typography, Chip,
} from '@mui/material';
import TooltipIconButton from '../../components/TooltipIconButton';
import { Add, Edit, ContentCopy, Visibility, VisibilityOff } from '@mui/icons-material';
import { DataGridPremium, GridColDef, GridRenderCellParams, GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { postbacksService } from '../../services/postbacks.service';
import { useModalState } from '../../hooks/useModalState';
import { buildEntityWhere, resolveSelectedIds, emptySelection } from '../../utils/entity-list';
import { useDebounced } from '../../hooks/useDebounced';
import EntityGridToolbar from '../../components/EntityGridToolbar';
import { Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, Divider } from '@mui/material';
import { useServerPagination } from '../../hooks/useServerPagination';
import { useGridHeight } from '../../hooks/useGridHeight';
import { useUrlState, codecs } from '../../hooks/useUrlState';

export default function PostbacksPage() {
  const queryClient = useQueryClient();
  const { setModal } = useModalState('postback');
  const [selection, setSelection] = useState<GridRowSelectionModel>(emptySelection);
  const [search, setSearch] = useUrlState('q', '', codecs.str);
  const [statuses, setStatuses] = useUrlState<string[]>('status', ['active'], codecs.statusCsv);
  const filterRef = useRef<HTMLDivElement>(null);
  const gridHeight = useGridHeight(filterRef);

  const debouncedSearch = useDebounced(search);
  const where = useMemo(
    () => buildEntityWhere('postback', { search: debouncedSearch, statuses, tagIds: [] }, ['name']),
    [debouncedSearch, statuses],
  );
  const { gridProps, offset, limit } = useServerPagination(50, where);
  const [usageFor, setUsageFor] = useState<{ id: string; name: string } | null>(null);
  const { data: usage, isLoading: usageLoading } = useQuery({
    queryKey: ['postback-usage', usageFor?.id],
    queryFn: () => postbacksService.usage(usageFor!.id),
    enabled: !!usageFor,
  });

  const { data, isFetching } = useQuery({
    queryKey: ['postbacks', where, offset, limit],
    queryFn: () => postbacksService.query({ where, offset, limit }),
    placeholderData: (prev) => prev,
  });

  const rows = useMemo(() => data?.data || [], [data]);
  const selectedIds = useMemo(() => resolveSelectedIds(selection, rows), [selection, rows]);
  const bulk = async (fn: (id: string) => Promise<unknown>) => {
    await Promise.all(selectedIds.map(fn));
    queryClient.invalidateQueries({ queryKey: ['postbacks'] });
    setSelection(emptySelection);
  };


  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1 },
    {
      field: 'usage', headerName: 'Used by', width: 190, sortable: false,
      renderCell: (p: GridRenderCellParams) => {
        const u = p.row.usage || { traffic_sources: 0, offers: 0 };
        const total = u.traffic_sources + u.offers;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {total === 0 ? (
              <Typography variant="caption" color="text.secondary">Not used</Typography>
            ) : (
              <Button size="small" onClick={() => setUsageFor({ id: p.row.id, name: p.row.name })}>
                {u.traffic_sources} traffic source{u.traffic_sources === 1 ? '' : 's'}, {u.offers} offer{u.offers === 1 ? '' : 's'}
              </Button>
            )}
          </Box>
        );
      },
    },
    { field: 'type', headerName: 'Type', width: 140, renderCell: (p: GridRenderCellParams) => {
      const label = { facebook_capi: 'Facebook CAPI', google_ads: 'Google Ads', bing_capi: 'Bing CAPI', twitter_capi: 'X (Twitter)' }[p.value as string] ?? 'URL';
      const color = { facebook_capi: 'info', google_ads: 'success', bing_capi: 'primary', twitter_capi: 'secondary' }[p.value as string] ?? 'default';
      return <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}><Chip label={label} size="small" color={color as any} /></Box>;
    }},
    { field: 'status', headerName: 'Status', width: 100, renderCell: (p: GridRenderCellParams) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}><Chip label={p.value} size="small" color={p.value === 'active' ? 'success' : p.value === 'archived' ? 'warning' : 'default'} /></Box>
    )},
    { field: 'created_at', headerName: 'Created', width: 110, valueFormatter: (v) => new Date(v).toLocaleDateString() },
    {
      field: 'actions', headerName: '', width: 70, sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0, alignItems: 'center', height: '100%' }}>
          <TooltipIconButton size="small" onClick={() => setModal({ open: true, id: params.row.id, reason: 'Update' })} title="Edit">
            <Edit sx={{ fontSize: 16 }} />
          </TooltipIconButton>
          <TooltipIconButton size="small" onClick={() => setModal({ open: true, id: params.row.id, reason: 'Duplicate' })} title="Duplicate">
            <ContentCopy sx={{ fontSize: 16 }} />
          </TooltipIconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box ref={filterRef} sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>Postbacks</Typography>
      </Box>

      <DataGridPremium
        rows={rows}
        columns={columns}
        loading={isFetching}
        {...gridProps(data?.total)}
        showToolbar
        slots={{ toolbar: EntityGridToolbar }}
        slotProps={{ toolbar: {
          search, onSearch: setSearch,
          statuses: { value: statuses, onChange: setStatuses },
          actions: [{ icon: Add, hint: 'Add postback', onClick: () => setModal({ open: true, reason: 'Create' }) }],
          selectedCount: selectedIds.length,
          pluralName: 'postbacks',
          selectionActions: [
            { icon: VisibilityOff, hint: 'Archive', onClick: () => bulk((id) => postbacksService.delete(id)), color: 'inherit' as const },
            { icon: Visibility, hint: 'Unarchive', onClick: () => bulk((id) => postbacksService.unarchive(id)), color: 'inherit' as const },
          ],
        } }}
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={selection}
        onRowSelectionModelChange={setSelection}
        onRowDoubleClick={(params: GridRowParams, event: React.MouseEvent) => {
          const target = event.target as HTMLElement;
          if (target.closest('[data-field="actions"]')) return;
          setModal({ open: true, id: params.row.id, reason: 'Update' });
        }}
        sx={{ bgcolor: 'background.paper', borderRadius: 2, height: gridHeight }}
      />
      <Dialog open={!!usageFor} onClose={() => setUsageFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Used by — {usageFor?.name}</DialogTitle>
        <DialogContent sx={{ '&.MuiDialogContent-root': { pt: 1 } }}>
          {usageLoading && <Typography variant="body2" color="text.secondary">Loading…</Typography>}
          {usage && (['traffic_sources', 'offers'] as const).map((key) => (
            <Box key={key} sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                {key === 'traffic_sources' ? 'Traffic sources' : 'Offers'} ({usage[key].length})
              </Typography>
              {usage[key].length === 0
                ? <Typography variant="body2" color="text.disabled">None</Typography>
                : (
                  <List dense disablePadding>
                    {usage[key].map((e) => (
                      <ListItem key={e.id} disableGutters><ListItemText primary={e.name} /></ListItem>
                    ))}
                  </List>
                )}
              <Divider sx={{ mt: 1 }} />
            </Box>
          ))}
        </DialogContent>
        <DialogActions><Button onClick={() => setUsageFor(null)}>Close</Button></DialogActions>
      </Dialog>

    </Box>
  );
}
